import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { Link } from "react-router";
import { Async, PageHead } from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api, type Usage } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useAsync } from "@/lib/hooks";

export function UsagePage() {
	const t = useT();
	const { locale } = useI18n();
	const state = useAsync<Usage>(() => api.usage(), []);

	return (
		<>
			<PageHead title={t("usage.title")} lede={t("usage.lede")} />
			<Async state={state}>
				{(u) => {
					const peak = Math.max(1, ...u.by_day.map((d) => d.cost_micros));
					return (
						<div className="stack">
							<div className="card">
								<h2>{t("usage.byDay")}</h2>
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("usage.day")}</TableHead>
											<TableHead className="num">{t("usage.calls")}</TableHead>
											<TableHead className="num">{t("usage.cost")}</TableHead>
											<TableHead className="num">{t("usage.billed")}</TableHead>
											<TableHead className="col-bar" />
										</TableRow>
									</TableHeader>
									<TableBody>
										{u.by_day.map((d) => (
											<TableRow key={d.day}>
												<TableCell className="mono">{d.day}</TableCell>
												<TableCell className="num">{fmt.number(locale, d.calls)}</TableCell>
												<TableCell className="num">{fmt.usd(locale, d.cost_micros)}</TableCell>
												<TableCell className="num">{fmt.usd(locale, d.billed_micros)}</TableCell>
												<TableCell>
													<div className="bar">
														<span style={{ width: `${(d.cost_micros / peak) * 100}%` }} />
													</div>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
							<div className="grid-2">
								<div className="card">
									<h2>{t("usage.byModel")}</h2>
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>{t("usage.model")}</TableHead>
												<TableHead className="num">{t("usage.calls")}</TableHead>
												<TableHead className="num">{t("usage.tokens")}</TableHead>
												<TableHead className="num">{t("usage.cost")}</TableHead>
												<TableHead className="num">{t("usage.billed")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{u.by_model.map((m) => (
												<TableRow key={m.model}>
													<TableCell className="mono">{m.model}</TableCell>
													<TableCell className="num">{fmt.number(locale, m.calls)}</TableCell>
													<TableCell className="num">
														{fmt.number(locale, m.input_tokens)} / {fmt.number(locale, m.output_tokens)}
													</TableCell>
													<TableCell className="num">{fmt.usd(locale, m.cost_micros)}</TableCell>
													<TableCell className="num">{fmt.usd(locale, m.billed_micros)}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
								<div className="stack">
									<div className="card">
										<h2>{t("usage.byKind")}</h2>
										<Table>
											<TableHeader>
												<TableRow>
													<TableHead>{t("usage.kind")}</TableHead>
													<TableHead className="num">{t("usage.calls")}</TableHead>
													<TableHead className="num">{t("usage.cost")}</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{u.by_kind.map((k) => (
													<TableRow key={k.kind}>
														<TableCell className="mono">{k.kind}</TableCell>
														<TableCell className="num">{fmt.number(locale, k.calls)}</TableCell>
														<TableCell className="num">{fmt.usd(locale, k.cost_micros)}</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</div>
									<div className="card">
										<h2>{t("usage.topOrgs")}</h2>
										<Table>
											<TableHeader>
												<TableRow>
													<TableHead>{t("orgs.name")}</TableHead>
													<TableHead className="num">{t("usage.calls")}</TableHead>
													<TableHead className="num">{t("usage.billed")}</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{u.top_orgs.map((o) => (
													<TableRow key={o.org_id}>
														<TableCell>
															<Link to={`/orgs/${o.org_id}`}>{o.name}</Link>
														</TableCell>
														<TableCell className="num">{fmt.number(locale, o.calls)}</TableCell>
														<TableCell className="num">{fmt.usd(locale, o.billed_micros)}</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</div>
								</div>
							</div>
						</div>
					);
				}}
			</Async>
		</>
	);
}
